 
import { Signal, TemplateRef, Type, WritableSignal } from '@angular/core';
import { Observable } from 'rxjs';

export enum SortOrderEnum {
  ASC = 'asc',
  DESC = 'desc',
  NONE = '',
}

/** Default page size for table paginators */
export const DEFAULT_TABLE_PAGE_SIZE = 20;

/** Shared page-size options for all table paginators */
export const TABLE_PAGE_SIZE_OPTIONS = [
  5, 10, 15, 20, 50, 100, 500, 1000,
] as const;

/**
 * Base Table Query interface
 * All table implementations should accept this query format
 */
export interface BaseTableQuery {
  skip: number;
  limit: number;
  sort_field?: string;
  sort_order?: SortOrderEnum;
  filters?: Record<string, any>;
  search?: string;
  projection?: string;
  [key: string]: unknown;
}

/**
 * Base Table Response interface
 * All table implementations should return this format
 */
export interface BaseTableResponse<T = any> {
  rows: T[];
  count: number;
}

/**
 * Base Table Column interface
 * Common column configuration across all implementations
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- phantom row type for extensions
export interface BaseTableColumn<T = any> {
  id: string;
  label: string;
  sortable?: boolean;
  filterable?: boolean;
  filterType?:
    | 'text'
    | 'number'
    | 'date'
    | 'boolean'
    | 'select'
    | 'multiselect'
    | 'infiniteMultiselect'
    | 'advancedSearch';
  sticky?: boolean;
  stickyEnd?: boolean;
  width?: string;
  minWidth?: string;
  maxWidth?: string;
  headerAlign?: 'left' | 'center' | 'right';
  cellsAlign?: 'left' | 'center' | 'right';
  hidden?: boolean;
  dataPath?: string;
  /**
   * Optional extra CSS class(es) applied to the column header label.
   */
  headerClass?: string;
  /**
   * Optional display formatter applied after the raw value is resolved.
   * Use this to present values in a human-readable form (e.g. boolean → "Yes" / "No")
   * without having to transform the underlying row data.
   * The return value is rendered as a plain string in the cell.
   */
  valueFormatter?: (value: unknown) => string;
  [key: string]: unknown; // Allow implementation-specific properties
}

/**
 * Base Table Pagination Config
 */
export interface BaseTablePaginationConfig {
  enabled: boolean;
  pageSize: number;
  pageSizeOptions: number[];
}

/**
 * Base Table Appearance Config
 */
export interface BaseTableAppearance {
  showRowNumbers?: boolean;
  showRowHover?: boolean;
  striped?: boolean;
  bordered?: boolean;
  dense?: boolean;
  /** Optional extra CSS class(es) for each data row. */
  getRowClass?: (row: unknown) => string;
  /**
   * Height behavior mode:
   * - 'fill-parent': Table fills 100% of parent container height (parent must be a flex column with bounded height)
   * - 'auto': Table grows based on content (safe default when the page does not establish a height chain)
   * @default 'auto'
   */
  heightMode?: 'fill-parent' | 'auto';
  /**
   * When false, table width stays at the sum of column widths (no stretching to 100%),
   * and extra space is not distributed to columns. Use in narrow contexts (e.g. row-details).
   * @default true
   */
  fillContainer?: boolean;
}

/**
 * Base Table Filter Option interface
 */
export interface BaseTableFilterOption {
  label: string;
  value: unknown;
}

export interface BaseTableCellContext<T = any> {
  $implicit: T;
  row: T;
  column: BaseTableColumn<T>;
  value: any;
  index: number;
}

export interface BaseTableRowDetailComponent<T = any> {
  row: WritableSignal<T>;
  table: WritableSignal<BaseTableService<T> | undefined>;
  onTabChange: (index: number) => void;
  refresh: () => void;
}

export interface BaseTableRowExpansionConfig<T = any> {
  enabled: boolean;
  expandOnRowClick?: boolean;
  multipleExpanded?: boolean;
  detailComponent?: Type<BaseTableRowDetailComponent<T>>;
}

/**
 * Base Table Action interface
 */
export interface BaseTableAction<T = any> {
  icon?: string;
  /** When true, render `icon` as a Material Icons font ligature instead of svgIcon. */
  iconFont?: boolean;
  /** Button type for icon/text actions (e.g. `danger` for destructive cancel/delete). */
  type?: 'primary' | 'secondary' | 'danger' | 'cancel';
  label?: string;
  tooltip?: string;
  handler: (row: T) => void;
  visible?: (row: T) => boolean;
  disabled?: (row: T) => boolean;
  /** When true, row action shows a spinner and is treated as disabled (e.g. async work in flight). */
  pending?: (row: T) => boolean;
}

/**
 * Base Table Actions Config
 */
export interface BaseTableActionsConfig<T = any> {
  enabled: boolean;
  actions?: BaseTableAction<T>[];
}

/**
 * Fetch function type
 */
export type BaseTableFetchFn<T = any, Q = BaseTableQuery> = (
  query: Q
) => Observable<BaseTableResponse<T>>;

/**
 * Query transform function type
 */
export type BaseTableQueryFn<Q = BaseTableQuery> = (query: BaseTableQuery) => Q;

/**
 * Base Table Service Interface
 *
 * All table implementations must implement this interface to ensure
 * they can be used interchangeably through the TableService factory.
 *
 * This provides a common contract for:
 * - Data management (loading, state)
 * - Configuration (columns, pagination, sorting, filtering)
 * - Operations (fetch, refresh, reset)
 * - Row interactions (selection, expansion)
 */
export interface BaseTableService<T = any> {
  // State signals - must be exposed for reactivity
  readonly isLoading: WritableSignal<boolean>;
  readonly data: WritableSignal<T[]>;
  readonly totalCount: WritableSignal<number>;
  readonly expandedRows: WritableSignal<Set<T>>;
  readonly selectedRows: WritableSignal<Set<T>>;
  /** Subject of the rows when they are printable; expanded rows offer printing. */
  readonly printSubject: WritableSignal<string | null>;

  // Computed values
  readonly columns: Signal<BaseTableColumn<T>[]>;
  readonly pagination: Signal<BaseTablePaginationConfig>;
  readonly appearance: Signal<BaseTableAppearance>;
  readonly query: Signal<BaseTableQuery>;

  // Configuration methods - must return 'this' for chaining
  setColumns(columns: BaseTableColumn<T>[]): this;
  setDataSource(fetchFn: BaseTableFetchFn<T>, queryFn?: BaseTableQueryFn): this;
  setFormatter?(
    formatter: (rows: T[]) => unknown[],
    replaceType?: boolean
  ): this;
  /** Defaults to {@link DEFAULT_TABLE_PAGE_SIZE} and {@link TABLE_PAGE_SIZE_OPTIONS} when omitted. */
  withPagination(pageSize?: number, pageSizeOptions?: number[]): this;
  setAppearance(appearance: Partial<BaseTableAppearance>): this;

  // Optional features (may not be supported by all implementations)
  withRowExpansion?(detailComponent?: any, options?: any): this;
  withActions?(config: any): this;

  // Query management
  setQuery(query: Partial<BaseTableQuery>): this;
  setPage(pageIndex: number): void;
  setPageSize(pageSize: number): void;
  setSort(field: string | null, direction: SortOrderEnum): void;
  setFilter?(columnId: string, filter: any): void;
  clearFilters?(): void;

  // Data operations
  fetch(force?: boolean): Promise<void>;
  /** Same data fetch as refresh(), without the loading overlay. */
  silentRefresh(): Promise<void>;
  refresh(): void;
  reset(): void;

  // Row operations
  toggleRow?(row: T): void;
  isRowExpanded?(row: T): boolean;
  collapseAll?(): void;

  // Utility methods
  getCellValue(row: T, column: BaseTableColumn<T>): any;
  getCurrentPageIndex(): number;
  onDestroy(): void;
}

/**
 * Configuration for mapping column IDs to their cell templates
 * This makes it easy to add new cell templates without modifying component logic
 */
export interface CellTemplateConfig<T = unknown> {
  columnId: string;
  template: TemplateRef<BaseTableCellContext<T>>;
}

/**
 * Helper function to apply cell templates to table columns
 * This is a scalable approach - just add new entries to the config array
 *
 * @param columns - Array of table columns
 * @param templateConfigs - Array of column ID to template mappings
 * @returns Updated columns array with templates applied
 */
export function applyCellTemplates<T = unknown>(
  columns: BaseTableColumn<T>[],
  templateConfigs: CellTemplateConfig<T>[]
): BaseTableColumn<T>[] {
  if (!templateConfigs || templateConfigs.length === 0) {
    return columns;
  }

  // Create a map for quick lookup
  const templateMap = new Map<string, TemplateRef<BaseTableCellContext<T>>>();
  templateConfigs.forEach(config => {
    templateMap.set(config.columnId, config.template);
  });

  // Apply templates to matching columns
  return columns.map(column => {
    const template = templateMap.get(column.id);
    if (template) {
      return {
        ...column,
        cellTemplate: template,
      } as BaseTableColumn<T>;
    }
    return column;
  });
}
